import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';

const API = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch(`${API}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Subscription failed');
      setStatus('success');
      setMessage('Check your inbox — we just sent you a note.');
      setEmail('');
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 6000);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Please try again.');
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  return (
    <footer>
      {/* Newsletter */}
      <section className="bg-white py-28 px-6">
        {/* Subtle separator above the newsletter block */}
        <div className="max-w-[1200px] mx-auto mb-24 border-t border-neutral-200" />
        <div className="max-w-2xl mx-auto text-center">
          <Reveal variant="fade">
            <p className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-6">Stay Connected</p>
          </Reveal>
          <Reveal delay={100}>
            <h3 className="font-display text-4xl md:text-5xl font-medium text-neutral-900 mb-5 tracking-tight">
              Join Our World
            </h3>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-neutral-600 text-[15px] leading-relaxed mb-10 max-w-md mx-auto">
              Be the first to discover new collections, exclusive offers, and stories from our atelier.
            </p>
          </Reveal>
          <Reveal delay={280}>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={status === 'loading'}
                data-testid="newsletter-email"
                className="flex-1 border border-neutral-300 px-5 py-4 text-sm focus:outline-none focus:border-neutral-900 transition-colors bg-white disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                data-testid="newsletter-submit"
                className="bg-neutral-900 text-white px-10 py-4 text-[11px] tracking-[0.22em] uppercase hover:bg-neutral-700 transition-colors disabled:opacity-60"
              >
                {status === 'loading'
                  ? 'Sending…'
                  : status === 'success'
                  ? 'Thank You'
                  : 'Subscribe'}
              </button>
            </form>
            {message && (
              <p
                data-testid="newsletter-message"
                className={`mt-6 text-sm ${status === 'error' ? 'text-red-600' : 'text-neutral-600'}`}
              >
                {message}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* Footer main */}
      <div className="bg-neutral-900 text-neutral-300 px-6 lg:px-12 py-20">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-12">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="font-display text-[22px] tracking-[0.18em] font-medium text-white">
              ANDOBRÉE
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-neutral-400 max-w-xs">
              Timeless elegance crafted with intention. Each piece tells a story of artisanal excellence and refined simplicity.
            </p>
          </div>
          <FooterCol
            title="Shop"
            links={[
              { label: 'New Arrivals', to: '/#new-arrivals' },
              { label: 'Pants', to: '/shop?category=bottoms' },
              { label: 'Swimwear', to: '/shop?category=swimwear' },
              { label: 'Bags', to: '/shop?category=bags' },
            ]}
          />
          <FooterCol
            title="About"
            links={[
              { label: 'Our Story', to: '/about' },
            ]}
          />
          <FooterCol
            title="Client Care"
            links={[
              { label: 'Contact Us', to: '/contact' },
              { label: 'Shipping & Returns', to: '/shipping' },
              { label: 'Size Guide', to: '/size-guide' },
              { label: 'FAQ', to: '/faq' },
            ]}
          />
        </div>
        <div className="max-w-[1600px] mx-auto border-t border-neutral-800 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] tracking-[0.2em] uppercase text-neutral-500">
          <p>© 2026 Andobrée. All Rights Reserved.</p>
          <div className="flex gap-8">
            <a
              href="https://www.instagram.com/andobree_official/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              Instagram
            </a>
            <a
              href="https://www.tiktok.com/@andobree3"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              TikTok
            </a>
            <a href="#" className="hover:text-white transition-colors">Twitter</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="text-[11px] tracking-[0.22em] uppercase text-white mb-6">{title}</h4>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.label}>
          <Link to={l.to} className="text-sm text-neutral-400 hover:text-white transition-colors">
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
